import { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/home.css";

const WIDTH = 1200;
const HEIGHT = 760;
const PADDING = 70;

const X_MIN = -8;
const X_MAX = 8;
const Y_MIN = -4;
const Y_MAX = 4;

const DRAW_DURATION = 5100;
const HOLD_DURATION = 2100;
const EXIT_DURATION = 3100;

function toSvgX(x) {
  return PADDING + ((x - X_MIN) / (X_MAX - X_MIN)) * (WIDTH - 2 * PADDING);
}

function toSvgY(y) {
  return HEIGHT - PADDING - ((y - Y_MIN) / (Y_MAX - Y_MIN)) * (HEIGHT - 2 * PADDING);
}

function gauss(x, mu, sigma, amp = 1) {
  return amp * Math.exp(-((x - mu) ** 2) / (2 * sigma ** 2));
}

function pulseTrain(x, period) {
  const half = period / 2;
  return ((((x + half) % period) + period) % period) - half;
}

function smoothStep(t) {
  return t * t * (3 - 2 * t);
}

function buildPath(fn, domain, samples = 1800) {
  const [start, end] = domain;
  let path = "";
  let started = false;

  for (let i = 0; i <= samples; i++) {
    const x = start + ((end - start) * i) / samples;
    const y = fn(x);

    if (!Number.isFinite(y)) {
      started = false;
      continue;
    }

    const px = toSvgX(x);
    const py = toSvgY(y);

    if (!started) {
      path += `M ${px} ${py}`;
      started = true;
    } else {
      path += ` L ${px} ${py}`;
    }
  }

  return path;
}

const FUNCTIONS = [
  {
    id: "sin",
    name: "Periodic signal",
    category: "Math / Signal",
    formula: "y = sin(x)",
    colorClass: "fn-signal",
    domain: [-8, 8],
    labelAt: [4.1, Math.sin(4.1)],
    fn: (x) => Math.sin(x),
  },
  {
    id: "cos",
    name: "Cosine",
    category: "Math / Signal",
    formula: "y = cos(x)",
    colorClass: "fn-signal-2",
    domain: [-8, 8],
    labelAt: [3.2, Math.cos(3.2)],
    fn: (x) => Math.cos(x),
  },
  {
    id: "pseudo-periodic",
    name: "Pseudo-periodic",
    category: "Signal processing",
    formula: "y = sin(x) + 0.35 sin(2.4x)",
    colorClass: "fn-pseudo",
    domain: [-8, 8],
    labelAt: [3.8, Math.sin(3.8) + 0.35 * Math.sin(2.4 * 3.8)],
    fn: (x) => Math.sin(x) + 0.35 * Math.sin(2.4 * x),
  },
  {
    id: "heart-wave",
    name: "Heart Wave",
    category: "Shape",
    formula: "y = |x|^(2/3) + 0.9 sin(95.28x)√(3 - x²)",
    colorClass: "fn-shape",
    domain: [-8, 8],
    labelAt: [0, 0],
    fn: (x) =>
      Math.pow(Math.abs(x), 2 / 3) +
      0.9 *
        Math.sin(95.28 * x) *
        Math.sqrt(Math.max(0, 3 - x * x)),
  },
  {
    id: "aperiodic-gaussian",
    name: "Aperiodic impulse",
    category: "Aperiodic signal",
    formula: "y = 3e^(-x²/2.2)",
    colorClass: "fn-aperiodic",
    domain: [-8, 8],
    labelAt: [1.9, 3 * Math.exp(-(1.9 ** 2) / 2.2)],
    fn: (x) => 3 * Math.exp(-(x * x) / 2.2),
  },
  {
    id: "lorentz",
    name: "Localized Peak",
    category: "Measurement / Response",
    formula: "y = 4 / (1 + x²)",
    colorClass: "fn-aperiodic-2",
    domain: [-8, 8],
    labelAt: [2.2, 4 / (1 + 2.2 * 2.2)],
    fn: (x) => 4 / (1 + x * x),
  },
  {
    id: "capacitor-charge",
    name: "Capacitor Charging",
    category: "Electricity",
    formula: "y = 4(1 - e^(-0.65(x+8))) - 4",
    colorClass: "fn-electric",
    domain: [0, 8],
    labelAt: [3.8, 4 * (1 - Math.exp(-0.65 * 3.8))],
    fn: (x) => 4 * (1 - Math.exp(-0.65 * x )),
  },
  {
    id: "rlc-damped",
    name: "Damped RLC Oscillation",
    category: "Electricity",
    formula: "y = 3e^(-0.22(x+8)) sin(3x)",
    colorClass: "fn-electric-2",
    domain: [-8, 8],
    labelAt: [3.1, 3 * Math.exp(-0.22 * (3.1 + 8)) * Math.sin(3 * 3.1)],
    fn: (x) => 3 * Math.exp(-0.22 * (x + 8)) * Math.sin(3 * x),
  },
  {
    id: "square-wave",
    name: "Smoothed Square Signal",
    category: "Electronics",
    formula: "y = 2 tanh(5 sin(1.6x))",
    colorClass: "fn-digital",
    domain: [-8, 8],
    labelAt: [2.7, 2 * Math.tanh(5 * Math.sin(1.6 * 2.7))],
    fn: (x) => 2 * Math.tanh(5 * Math.sin(1.6 * x)),
  },
  {
    id: "heartbeat",
    name: "Heartbeat",
    category: "Medicine / Biomedical",
    formula: "Simplified ECG",
    colorClass: "fn-heart",
    domain: [-8, 8],
    labelAt: [4.5, -0.15],
    fn: (x) => {
      const u = pulseTrain(x, 3.3);
      return (
        gauss(u, -1.15, 0.18, 0.25) +
        gauss(u, -0.14, 0.05, -0.55) +
        gauss(u, 0.0, 0.03, 3.8) +
        gauss(u, 0.16, 0.05, -0.9) +
        gauss(u, 0.95, 0.24, 0.7) -
        0.12
      );
    },
  },
  {
    id: "respiration",
    name: "Breathing",
    category: "Medicine / Physiology",
    formula: "y = 1.4 sin(0.8x) + 0.15 sin(2.4x)",
    colorClass: "fn-med",
    domain: [-8, 8],
    labelAt: [4.0, 1.4 * Math.sin(0.8 * 4.0) + 0.15 * Math.sin(2.4 * 4.0)],
    fn: (x) => 1.4 * Math.sin(0.8 * x) + 0.15 * Math.sin(2.4 * x),
  },
  {
    id: "seismic-wave",
    name: "Damped Seismic Wave",
    category: "Seismology",
    formula: "y = 2.8 e^(-0.18(x+8)) sin(6x)",
    colorClass: "fn-seismic",
    domain: [-8, 8],
    labelAt: [2.8, 2.8 * Math.exp(-0.18 * (2.8 + 8)) * Math.sin(6 * 2.8)],
    fn: (x) => 2.8 * Math.exp(-0.18 * (x + 8)) * Math.sin(6 * x),
  },
  {
    id: "building-response",
    name: "Building Response",
    category: "Civil Engineering",
    formula: "y = 3.2 e^(-0.25(x+8)) sin(2.3x)",
    colorClass: "fn-building",
    domain: [-8, 8],
    labelAt: [3.2, 3.2 * Math.exp(-0.25 * (3.2 + 8)) * Math.sin(2.3 * 3.2)],
    fn: (x) => 3.2 * Math.exp(-0.25 * (x + 8)) * Math.sin(2.3 * x),
  },
  {
    id: "spring-underdamped",
    name: "Underdamped Spring",
    category: "Mechanics",
    formula: "y = 3e^(-0.22(x+8)) cos(2.7x)",
    colorClass: "fn-spring",
    domain: [-8, 8],
    labelAt: [2.6, 3 * Math.exp(-0.22 * (2.6 + 8)) * Math.cos(2.7 * 2.6)],
    fn: (x) => 3 * Math.exp(-0.22 * (x + 8)) * Math.cos(2.7 * x),
  },
  {
    id: "spring-critical",
    name: "Critical Damping",
    category: "Mechanics / Control",
    formula: "y = 3.5(x+8)e^(-0.8(x+8)) - 2.5",
    colorClass: "fn-critical",
    domain: [-8, 8],
    labelAt: [-5.2, 3.5 * ((-5.2 + 8) * Math.exp(-0.8 * (-5.2 + 8))) - 2.5],
    fn: (x) => 3.5 * ((x + 8) * Math.exp(-0.8 * (x + 8))) - 2.5,
  },
  {
    id: "spring-overdamped",
    name: "Overdamped Regime",
    category: "Mechanics / Control",
    formula: "y = 3e^(-0.28(x+8)) - 3.2e^(-0.65(x+8)) - 1.2",
    colorClass: "fn-overdamped",
    domain: [-8, 8],
    labelAt: [-2.0, 3 * Math.exp(-0.28 * (-2.0 + 8)) - 3.2 * Math.exp(-0.65 * (-2.0 + 8)) - 1.2],
    fn: (x) => 3 * Math.exp(-0.28 * (x + 8)) - 3.2 * Math.exp(-0.65 * (x + 8)) - 1.2,
  },
  {
    id: "arctan",
    name: "Saturation",
    category: "Control / Instrumentation",
    formula: "y = 2 arctan(x)",
    colorClass: "fn-control",
    domain: [-8, 8],
    labelAt: [4.6, 2 * Math.atan(4.6)],
    fn: (x) => 2 * Math.atan(x),
  },
  {
    id: "newton5",
    name: "Newton Cradle (5 balls)",
    category: "Physics",
    formula: "phase shifted cos",
    colorClass: "fn-physics",
    domain: [-8, 8],
    labelAt: [2, Math.cos(2)],
    fn: (t) => {
      return Math.cos(t) * Math.sign(Math.cos(t));
    },
  }
];

function AxisLayer() {
  const xTicks = [];
  const yTicks = [];
  const tickSize = 10;

  for (let x = Math.ceil(X_MIN); x <= Math.floor(X_MAX); x++) xTicks.push(x);
  for (let y = Math.ceil(Y_MIN); y <= Math.floor(Y_MAX); y++) yTicks.push(y);

  return (
    <>
      <defs>
        <marker
          id="axis-arrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#222" />
        </marker>
      </defs>

      {xTicks.map((x) => (
        <g key={`grid-x-${x}`}>
          <line
            x1={toSvgX(x)}
            y1={toSvgY(Y_MIN)}
            x2={toSvgX(x)}
            y2={toSvgY(Y_MAX)}
            className="math-grid-line-light"
          />
          {x !== 0 && (
            <>
              <line
                x1={toSvgX(x)}
                y1={toSvgY(0) - tickSize / 2}
                x2={toSvgX(x)}
                y2={toSvgY(0) + tickSize / 2}
                className="math-axis-tick"
              />
              <text
                x={toSvgX(x)}
                y={toSvgY(0) + 24}
                textAnchor="middle"
                className="math-axis-label-dark"
              >
                {x}
              </text>
            </>
          )}
        </g>
      ))}

      {yTicks.map((y) => (
        <g key={`grid-y-${y}`}>
          <line
            x1={toSvgX(X_MIN)}
            y1={toSvgY(y)}
            x2={toSvgX(X_MAX)}
            y2={toSvgY(y)}
            className="math-grid-line-light"
          />
          {y !== 0 && (
            <>
              <line
                x1={toSvgX(0) - tickSize / 2}
                y1={toSvgY(y)}
                x2={toSvgX(0) + tickSize / 2}
                y2={toSvgY(y)}
                className="math-axis-tick"
              />
              <text
                x={toSvgX(0) - 16}
                y={toSvgY(y) + 5}
                textAnchor="end"
                className="math-axis-label-dark"
              >
                {y}
              </text>
            </>
          )}
        </g>
      ))}

      <line
        x1={toSvgX(X_MIN)}
        y1={toSvgY(0)}
        x2={toSvgX(X_MAX)}
        y2={toSvgY(0)}
        className="math-axis-main-dark"
        markerEnd="url(#axis-arrow)"
      />

      <line
        x1={toSvgX(0)}
        y1={toSvgY(Y_MIN)}
        x2={toSvgX(0)}
        y2={toSvgY(Y_MAX)}
        className="math-axis-main-dark"
        markerEnd="url(#axis-arrow)"
      />

      <text
        x={toSvgX(X_MAX) - 10}
        y={toSvgY(0) - 14}
        className="math-axis-title-dark"
      >
        x
      </text>

      <text
        x={toSvgX(0) + 14}
        y={toSvgY(Y_MAX) + 4}
        className="math-axis-title-dark"
      >
        y
      </text>
    </>
  );
}

function FunctionLabel({ fn }) {
  const x = toSvgX(fn.labelAt[0]);
  const y = toSvgY(fn.labelAt[1]);

  return (
    <g transform={`translate(${x + 12}, ${y - 12})`} className="math-inline-label-dark">
      <text className="math-inline-category-dark">{fn.category}</text>
      <text className="math-inline-name-dark" y="22">{fn.name}</text>
      <text className="math-inline-formula-dark" y="42">{fn.formula}</text>
    </g>
  );
}

export default function MathFunctionsShowcase() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState("draw");
  const [animProgress, setAnimProgress] = useState(0);

  const rafRef = useRef(null);
  const phaseStartRef = useRef(0);

  const currentFn = FUNCTIONS[currentIndex];

  const currentPath = useMemo(() => {
    return buildPath(currentFn.fn, currentFn.domain, 1800);
  }, [currentFn]);

  useEffect(() => {
    phaseStartRef.current = performance.now();
    setAnimProgress(0);

    const durations = {
      draw: DRAW_DURATION,
      hold: HOLD_DURATION,
      exit: EXIT_DURATION,
    };

    const tick = (now) => {
      const elapsed = now - phaseStartRef.current;
      const raw = Math.min(elapsed / durations[phase], 1);
      const progress = smoothStep(raw);

      setAnimProgress(progress);

      if (raw >= 1) {
        if (phase === "draw") {
          setPhase("hold");
        } else if (phase === "hold") {
          setPhase("exit");
        } else {
          setCurrentIndex((prev) => (prev + 1) % FUNCTIONS.length);
          setPhase("draw");
        }
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [phase, currentIndex]);

  let revealX = 0;
  let revealWidth = WIDTH;

  if (phase === "draw") {
    revealWidth = WIDTH * animProgress;
  } else if (phase === "hold") {
    revealWidth = WIDTH;
  } else if (phase === "exit") {
    revealX = WIDTH * animProgress;
    revealWidth = WIDTH * (1 - animProgress);
  }

  return (
    <section className="math-minimal-section">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="math-minimal-graph"
        aria-label="Fonctions mathématiques et d’ingénierie animées"
        shapeRendering="geometricPrecision"
      >
        <defs>
          <clipPath id={`curve-reveal-${currentFn.id}`}>
            <rect
              x={revealX}
              y="0"
              width={Math.max(0, revealWidth)}
              height={HEIGHT}
            />
          </clipPath>
        </defs>

        <AxisLayer />

        <g clipPath={`url(#curve-reveal-${currentFn.id})`}>
          <path
            d={currentPath}
            className={`math-function-line-glow ${currentFn.colorClass}`}
          />
          <path
            d={currentPath}
            className={`math-function-line ${currentFn.colorClass}`}
          />
        </g>

        <FunctionLabel fn={currentFn} />
      </svg>
    </section>
  );
}